import jsPDF from 'jspdf';
import { IRegulation, IAmendment } from '../../contracts/amendment.contract';
import { ICriteria } from '../../contracts/criteria.contract';
import { DocumentType, PrintMode } from '../../constants';
import getDateString from '../shared/date-string-convertion';
import { MergeRegulationDescriptionETC } from '../shared';

interface RegulationPrintProps {
    filteredRegulations: IRegulation[];
    filters: ICriteria;
    documentType: string;
    printMode: string;
    vesselNames?: string[];
}

const RegulationPrint = ({ filteredRegulations, filters, documentType, printMode, vesselNames = [] }: RegulationPrintProps) => {
    let pageNumber = 1;
    let y = 7;

    const doc = new jsPDF();

    const addPage = () => {
        y = 10;
        doc.addPage();
        pageNumber += 1;
    };

    const addText = (text: string | string[], yToAdd: number) => {
        if (y + yToAdd > 280) {
            addPage();
        }
        if (Array.isArray(text)) {
            text.forEach((line) => {
                doc.text(line, 20, y);
                y += 5;
            });
        } else {
            doc.text(text, 20, y);
        }
    };

    const increaseY = (value: number) => {
        y += value;
        if (y > 280) {
            addPage();
        }
        return y;
    };

    const getLabel = (filterType: string): string => {
        return `${filterType}`;
    };

    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    increaseY(10);
    const amendmentsLength = filteredRegulations.flatMap((f) => f.Amendments).length;
    doc.text(`Showing ${amendmentsLength} amendments across ${filteredRegulations.length} regulations`, 15, y);

    const labelList: string[] = [];
    Object.keys(filters).forEach((filterType) => {
        const filterValue = filters[filterType as keyof ICriteria];
        if (filterValue === true) {
            labelList.push(getLabel(filterType));
        }
    });

    increaseY(10);
    doc.setFontSize(11);
    if (documentType === DocumentType.Tracker) {
        if (labelList.length > 0) {
            doc.text('Filters Selected: ', 15, y);
            increaseY(5);

            const description = doc.splitTextToSize(labelList.join(' | ').replaceAll('≥', '>='), 165);
            addText(description, 2 * description.length);
            increaseY(2 * description.length);
        }
    } else if (documentType === DocumentType.Vessel) {
        if (vesselNames.length > 0) {
            doc.text('Filters Selected: ', 15, y);
            increaseY(5);

            const vesselNamesText = doc.splitTextToSize(vesselNames.join(' | ').replaceAll('≥', '>='), 165);
            addText(vesselNamesText, 2 * vesselNamesText.length);
            increaseY(2 * vesselNamesText.length);
        }
    }

    const regulationsData: any = [];

    filteredRegulations.forEach((filteredRegulation) => {
        const uniqueRegulationsMap = MergeRegulationDescriptionETC(filteredRegulation);

        const someText = filteredRegulation.RegulationName.replace(/[\r\n]+/g, ' ');
        regulationsData.push({ title: filteredRegulation.RegulationName.replace(/[\r\n]+/g, ' '), pageNumber });
        doc.setFontSize(14);
        doc.setTextColor(0, 0, 0);

        increaseY(10);
        const pageWidth = doc.internal.pageSize.getWidth();
        const margin = 10;
        const textLines = doc.splitTextToSize(someText, pageWidth - 2 * margin);
        doc.text(textLines, 15, y);
        increaseY(15);

        doc.setFontSize(11);
        doc.setTextColor(0, 42, 76);

        doc.textWithLink(filteredRegulation.RegulationDocName.replace(/[\r\n]+/g, ' '), 15, y, { url: filteredRegulation.RegulationUrl });
        increaseY(7);

        doc.setLineWidth(0.25);
        doc.setDrawColor(150);
        doc.line(15, y, 190, y);
        increaseY(5);

        doc.setTextColor(0, 0, 0);
        doc.text(`${filteredRegulation.Amendments.length} Amendment(s)`, 15, y);
        increaseY(8);

        Object.values(uniqueRegulationsMap).forEach((amendmentGroup: IAmendment[]) => {
            const classLabel = amendmentGroup[0].OperationalOrHardware == 'OPERATIONAL' ? 'Operational' : 'Hardware';
            const requirementLabel = amendmentGroup[0].MandatoryOrGuide == 'MANDATORY' ? 'requirement' : 'guideline';

            const retroActiveString = amendmentGroup[0].AppToAge;

            const firstLine = `${classLabel} ${requirementLabel} effective ${amendmentGroup[0].ComplianceDate} - applies ${retroActiveString}`;

            // determine the vessel name based on the number of items in the amendmentGroup
            const amendmentGroupVesselName = Array.from(new Set(amendmentGroup.map((item) => item.ShipType.Name)));
            let vesselName = '';
            if (amendmentGroupVesselName.length === 1) {
                vesselName = amendmentGroup[0].ShipType.Name
                    ? ['All Vessels', 'All Ships'].includes(amendmentGroup[0].ShipType.Name)
                        ? amendmentGroup[0].ShipType.Name
                        : `${amendmentGroup[0].ShipType.Name}s`
                    : 'All Vessels';
            }
            if (amendmentGroupVesselName.length === 2) {
                vesselName = amendmentGroupVesselName.join(' and ');
            }
            if (amendmentGroupVesselName.length > 2) {
                vesselName = amendmentGroupVesselName.join(', ');
            }

            const secondLine = doc.splitTextToSize(`Applies to ${vesselName} with a ${getDateString(amendmentGroup)}`, 165);

            doc.text(firstLine, 20, y);
            increaseY(5);

            doc.text(secondLine, 20, y);
            if (amendmentGroup.length > 2) {
                increaseY(20);
            } else {
                increaseY(11);
            }
            const description = doc.splitTextToSize(amendmentGroup[0].Description.replaceAll('≥', '>='), 165);
            addText(description, 5 * description.length);
            increaseY(2 * description.length);
            doc.line(20, y - 2, 180, y - 2);
            increaseY(1);

            increaseY(1);
        });
    });

    if (printMode === PrintMode.View) {
        addPage();
    } else if (printMode === PrintMode.Print) {
        increaseY(1);
    }

    regulationsData.forEach((regulation: { title: string; pageNumber: any }) => {
        doc.outline.add(null, regulation.title, { pageNumber: regulation.pageNumber });
    });

    doc.save('regulation-summary.pdf');
};

export default RegulationPrint;
